import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';

// Get API URL from environment
const API_URL = process.env.NODE_ENV === 'production'
  ? ''
  : process.env.REACT_APP_API_URL || 'http://localhost:8040';

const Patients = () => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientDetail, setShowPatientDetail] = useState(false);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [patientMedia, setPatientMedia] = useState([]);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchPatients();
  }, [currentPage, searchTerm]);

  // Handle phone number query parameter for direct navigation to patient details
  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    if (phoneParam && patients.length > 0) {
      // Find patient by phone number
      const patient = patients.find(p => p.phone_number === phoneParam);
      if (patient) {
        // Navigate to patient details page
        navigate(`/patients/${patient.id}`);
        return;
      }
    }
  }, [searchParams, patients, navigate]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('Fetching patients with token:', token ? 'Present' : 'Missing');

      const response = await fetch(
        `${API_URL}/api/patients?page=${currentPage}&limit=${itemsPerPage}&search=${encodeURIComponent(searchTerm)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch patients: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('Non-JSON response:', responseText);
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();
      console.log('Patients data:', data);
      setPatients(data.items || []);
      setTotalPages(Math.ceil((data.total || 0) / itemsPerPage));
    } catch (err) {
      setError(err.message);
      console.error('Error fetching patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientDetails = async (patientId) => {
    try {
      const token = localStorage.getItem('token');

      // Fetch appointments
      const appointmentsResponse = await fetch(
        `${API_URL}/api/patients/${patientId}/appointments`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (appointmentsResponse.ok) {
        const appointmentsData = await appointmentsResponse.json();
        setPatientAppointments(appointmentsData.items || []);
      }

      // Fetch media
      const mediaResponse = await fetch(
        `${API_URL}/api/patients/${patientId}/media`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (mediaResponse.ok) {
        const mediaData = await mediaResponse.json();
        setPatientMedia(mediaData.items || []);
      }
    } catch (err) {
      console.error('Error fetching patient details:', err);
    }
  };

  const handlePatientClick = async (patient) => {
    setSelectedPatient(patient);
    setShowPatientDetail(true);
    await fetchPatientDetails(patient.id);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && patients.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#075e54]"></div>
        <p className="ml-4 text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (showPatientDetail && selectedPatient) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setShowPatientDetail(false);
                setSelectedPatient(null);
                setPatientAppointments([]);
                setPatientMedia([]);
              }}
              className="flex items-center text-[#075e54] hover:text-[#064e45]"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('common.back', 'Back to Patients')}
            </button>
          </div>
        </div>

        {/* Patient Info */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('patient.details')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">{t('patient.name')}</p>
              <p className="text-lg text-gray-900">{selectedPatient.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('patient.phone')}</p>
              <p className="text-lg text-gray-900">{selectedPatient.phone_number}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('patient.languagePreference', 'Language Preference')}</p>
              <p className="text-lg text-gray-900">{selectedPatient.language_preference || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('patient.lastContact', 'Last Contact')}</p>
              <p className="text-lg text-gray-900">{formatDate(selectedPatient.updated_at)}</p>
            </div>
          </div>
          {selectedPatient.notes && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-500">Notes</p>
              <p className="text-gray-900">{selectedPatient.notes}</p>
            </div>
          )}
          {selectedPatient.medical_history && selectedPatient.medical_history.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-500">Medical History</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {selectedPatient.medical_history.map((item, index) => (
                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Appointments */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Appointments</h3>
          {patientAppointments.length === 0 ? (
            <p className="text-gray-500">No appointments found.</p>
          ) : (
            <div className="space-y-3">
              {patientAppointments.map((appointment) => (
                <div key={appointment.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {appointment.doctor_name || 'No doctor assigned'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {formatDate(appointment.appointment_date)} at {formatTime(appointment.appointment_start_time)}
                      </p>
                      {appointment.remarks && (
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{appointment.remarks}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                      appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {appointment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Media */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Media</h3>
          {patientMedia.length === 0 ? (
            <p className="text-gray-500">No media found.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {patientMedia.map((media) => (
                <div key={media.id} className="border rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {media.media_type === 'image' ? (
                        <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M4 4h16v16H4V4zm2 2v12h12V6H6zm2 10l2-3 2 2 4-5 2 2v4H8v-0z"/>
                        </svg>
                      ) : media.media_type === 'audio' ? (
                        <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM8 11a4 4 0 0 0 8 0h2a6 6 0 0 1-12 0h2zm4 6v3h-2v-3h2z"/>
                        </svg>
                      ) : (
                        <svg className="w-8 h-8 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {media.filename || 'Untitled'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(media.created_at)}
                      </p>
                    </div>
                    {media.media_url && (
                      <a
                        href={media.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#075e54] hover:text-[#064e45] text-sm"
                      >
                        View
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">{t('patient.title')}</h1>
      </div>

      {/* Search */}
      <SearchBar onSearch={handleSearch} placeholder={t('patient.search')} />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Patients List */}
      <div className="bg-white rounded-lg shadow-md">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#075e54]"></div>
          </div>
        ) : patients.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No patients found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria.' : 'No patients have been registered yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('patient.patient', 'Patient')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('patient.phone')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('patient.language', 'Language')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('patient.lastContact', 'Last Contact')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {patients.map((patient) => (
                    <tr key={patient.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-[#075e54] flex items-center justify-center text-white font-medium">
                              {patient.name ? patient.name.charAt(0).toUpperCase() : patient.phone_number.slice(-2)}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {patient.name || 'Unknown'}
                            </div>
                            {patient.medical_history && patient.medical_history.length > 0 && (
                              <div className="text-sm text-gray-500">
                                {patient.medical_history.slice(0, 2).join(', ')}
                                {patient.medical_history.length > 2 && '...'}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.phone_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {patient.language_preference || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(patient.updated_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handlePatientClick(patient)}
                          className="text-[#075e54] hover:text-[#064e45]"
                        >
                          {t('patient.viewDetails', 'View Details')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Patients;
